import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import {
  FRCWidgetCompleteEvent,
  FriendlyCaptchaSDK,
  CreateWidgetOptions,
  WidgetErrorData,
  FRCWidgetErrorEventData,
} from '@friendlycaptcha/sdk';
import { FRIENDLY_CAPTCHA_SITE_KEY } from '@config';

const sdk = new FriendlyCaptchaSDK({
  apiEndpoint: 'global', // Set this to "eu" if you're using the EU endpoint.
  disableEvalPatching: false, // Set this to true if your React application uses eval in dev mode which is common in many frameworks.
});

type Props = Omit<CreateWidgetOptions, 'element'> & {
  onComplete?: (response: string) => void;
  onError?: (error: WidgetErrorData) => void;
  onExpire?: () => void;
};

type Ref = {
  reset: () => void;
};

export const FriendlyCaptcha = forwardRef<Ref, Props>((props, ref) => {
  const captchaRef = useRef<HTMLDivElement>(null);
  const { onComplete, onError, onExpire } = props;

  useEffect(() => {
    if (captchaRef.current) {
      if (onComplete) {
        captchaRef.current.addEventListener('frc:widget.complete', (e) => {
          onComplete!((e as FRCWidgetCompleteEvent).detail.response);
        });
      }

      if (onError) {
        captchaRef.current.addEventListener('frc:widget.error', (e) => {
          onError!((e as CustomEvent<FRCWidgetErrorEventData>).detail.error);
        });
      }

      if (onExpire) {
        captchaRef.current.addEventListener('frc:widget.expire', () => {
          onExpire!();
        });
      }
    }
  }, [onComplete, onError, onExpire]);

  useEffect(() => {
    if (captchaRef.current) {
      console.count('created');
      const captcha = sdk.createWidget({
        element: captchaRef.current,
        sitekey: FRIENDLY_CAPTCHA_SITE_KEY,
      });
      return () => captcha?.destroy();
    }
  }, []);

  // Expose the reset method to the parent component
  useImperativeHandle(ref, () => ({
    reset: () => {
      // @ts-expect-error - TS doesn't know about the reset method
      captchaRef.current?.reset();
    },
  }));

  return <Box sx={{ width: '100% !important' }} ref={captchaRef} />;
});
